import { ArrayHelpers, Field, FieldArray, FieldProps, Formik, FormikProps } from 'formik';
import { DeclineIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { Grid, IconButton, Theme } from '@mui/material';
import { PoolButton, PoolModal, PoolTextField, PoolTypography, ScrollToFieldError } from '@synergy/components';
import { ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { Term } from '../../models/term';
import { TermFormValue } from '../../models/term-form-value';
import { TermRequestValue } from '../../api/models/term-request-value';
import { TermsValidationSchema } from './terms-validation-schema';

interface FormValues {
    terms: TermFormValue[];
}

type TermsArrayHelper = ArrayHelpers & {
    form: FormikProps<FormValues>;
    name: string;
};

interface TermsBaseModalProps {
    terms: Term[];
    title: ReactNode;
    inputPlaceholder: string;
    newTermTitle: ReactNode;
    onClose: () => void;
    onSubmit: (terms: TermRequestValue[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
    term: {
        display: 'flex',
        alignItems: 'center',

        '& > div:first-child': {
            flexGrow: 1,
            marginRight: theme.spacing(2),
        },
    },
    usedBySuppliersText: {
        marginLeft: 12,
        color: theme.palette.grey[700],
    },
    contentSpacer: {
        marginTop: theme.spacing(6),
    },
}));

export const TermsBaseModal = (props: TermsBaseModalProps) => {
    const { title, onClose, onSubmit, terms, inputPlaceholder, newTermTitle } = props;
    const classes = useStyles();

    const initialValues: { terms: Term[] } = { terms: terms ?? [] };

    const handleSubmit = (values: { terms: Term[] }) => {
        onSubmit(values.terms.map(term => ({ id: term.id, value: term.value })));
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={TermsValidationSchema}
        >
            <>
                <ScrollToFieldError />
                <FieldArray name="terms">
                    {(helper: TermsArrayHelper) => (
                        <PoolModal
                            title={title}
                            actionChildren={[
                                <PoolButton key="1" onClick={props.onClose} variant="text" styleType="firstLevel">
                                    <FormattedMessage id="app.common.cancel" />
                                </PoolButton>,
                                <PoolButton
                                    key="2"
                                    onClick={helper.form.submitForm}
                                    type="submit"
                                    styleType="firstLevel"
                                >
                                    <FormattedMessage id="app.common.save" />
                                </PoolButton>,
                            ]}
                            isVisible={true}
                            handleClose={onClose}
                            width="lg"
                        >
                            {(hasTitleDivider: boolean) => (
                                <Grid
                                    container={true}
                                    spacing={2}
                                    className={hasTitleDivider ? classes.contentSpacer : ''}
                                >
                                    {helper.form.values.terms.map((term, index) => (
                                        <Grid container={true} item={true} xs={12} key={index}>
                                            <Grid item={true} xs={12} className={classes.term}>
                                                <Field name={`terms.${index}.value`}>
                                                    {({ meta, field }: FieldProps<string>) => (
                                                        <PoolTextField
                                                            {...field}
                                                            placeholder={inputPlaceholder}
                                                            error={meta.touched && !!meta.error}
                                                            helperText={
                                                                meta.touched && meta.error ? (
                                                                    <FormattedMessage id={meta.error} />
                                                                ) : null
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                                <div style={{ width: 44 }}>
                                                    {(term.usedBySupplierIds ?? []).length === 0 && (
                                                        <IconButton onClick={() => helper.remove(index)}>
                                                            <DeclineIcon />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            </Grid>
                                            {(term.usedBySupplierIds ?? []).length > 0 && (
                                                <Grid item={true} xs={12}>
                                                    <PoolTypography
                                                        typography="caption"
                                                        id="app.contactsAndOrganizations.detail.usedBySuppliers"
                                                        values={{
                                                            value: (term?.usedBySupplierIds ?? []).length,
                                                        }}
                                                        className={classes.usedBySuppliersText}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    ))}

                                    <Grid item={true} xs={12}>
                                        <PoolButton
                                            styleType="thirdLevel"
                                            startIcon={<AddIcon color="primary" />}
                                            onClick={() =>
                                                helper.push({
                                                    value: '',
                                                })
                                            }
                                        >
                                            {newTermTitle}
                                        </PoolButton>
                                    </Grid>
                                </Grid>
                            )}
                        </PoolModal>
                    )}
                </FieldArray>
            </>
        </Formik>
    );
};
